import { useToast } from "./useToast";
import { APIError } from "@/APIClient";

type AIGuardErrorCode =
  | "AI_REPORT_GENERATION_IN_PROGRESS"
  | "AI_IQOA_ASSOCIATION_IN_PROGRESS"
  | "AI_DOCUMENT_EXTRACTION_IN_PROGRESS"
  | "AI_OPERATION_IN_PROGRESS";

const AI_GUARD_ERROR_CODES: AIGuardErrorCode[] = [
  "AI_REPORT_GENERATION_IN_PROGRESS",
  "AI_IQOA_ASSOCIATION_IN_PROGRESS",
  "AI_DOCUMENT_EXTRACTION_IN_PROGRESS",
  "AI_OPERATION_IN_PROGRESS",
];

/**
 * Composable for handling AI guard errors from the backend.
 * When an AI operation is in progress, certain mutations are blocked.
 * This composable provides a way to detect and display these errors.
 */
export function useAIGuardError() {
  const { showWarning } = useToast();

  /**
   * Check if an error is an AI guard error (409 Conflict with specific error codes)
   */
  function isAIGuardError(error: unknown): error is APIError {
    if (!(error instanceof APIError)) return false;
    if (error.status !== 409) return false;

    const errorCode = (error.response as { error?: string })?.error;
    return AI_GUARD_ERROR_CODES.includes(errorCode as AIGuardErrorCode);
  }

  /**
   * Get the error code from an AI guard error
   */
  function getAIGuardErrorCode(error: APIError): AIGuardErrorCode | null {
    const errorCode = (error.response as { error?: string })?.error;
    if (AI_GUARD_ERROR_CODES.includes(errorCode as AIGuardErrorCode)) {
      return errorCode as AIGuardErrorCode;
    }
    return null;
  }

  /**
   * Handle an AI guard error by showing a translated warning message
   * @returns true if the error was an AI guard error and was handled
   */
  function handleAIGuardError(error: unknown): boolean {
    if (!isAIGuardError(error)) return false;

    const errorCode = getAIGuardErrorCode(error);
    if (!errorCode) return false;

    showWarning(`report:ai.errors.${errorCode}`);
    return true;
  }

  /**
   * Create an error handler that checks for AI guard errors first,
   * then falls back to a default handler if not an AI guard error.
   */
  function createErrorHandler(fallbackMessage?: string) {
    return (error: unknown) => {
      if (handleAIGuardError(error)) return;

      // Fallback to default message
      if (fallbackMessage) {
        showWarning(fallbackMessage);
      }
    };
  }

  return {
    isAIGuardError,
    getAIGuardErrorCode,
    handleAIGuardError,
    createErrorHandler,
  };
}
